<template>
  <Transition name="banner-slide">
    <div v-if="isVisible" class="welcome-banner-wrapper">
      <div class="welcome-banner">
        <!-- 渐变背景 -->
        <div class="banner-gradient"></div>

        <!-- 内容 -->
        <div class="banner-content">
          <!-- 图标 -->
          <div class="banner-icon">
            <div class="i-lucide-music text-2xl"></div>
          </div>

          <!-- 文字内容 -->
          <div class="banner-text">
            <h3 class="banner-title">
              {{ t('welcome.newUserBonus') }}
            </h3>
            <p class="banner-subtitle">
              {{ t('welcome.freeSongs') }}
            </p>
          </div>

          <!-- 操作按钮 -->
          <div class="banner-actions">
            <button @click="handleStartCreating" class="btn-primary">
              {{ t('welcome.startCreating') }}
              <div class="i-lucide-arrow-right ml-1"></div>
            </button>
            <button @click="handleClose" class="btn-close" :aria-label="t('common.close')">
              <div class="i-lucide-x"></div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from '#imports';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from '#imports';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const isVisible = ref(false);
const STORAGE_KEY = 'welcome_banner_shown';
const DELAY_MS = 3000; // 3秒延迟

onMounted(() => {
  // 检查是否已经显示过
  if (typeof window !== 'undefined') {
    const hasShown = localStorage.getItem(STORAGE_KEY);

    if (!hasShown) {
      // 延迟显示横幅
      setTimeout(() => {
        isVisible.value = true;
      }, DELAY_MS);
    }
  }
});

// 监听路由变化，如果进入 create 页面则自动关闭
watch(() => route.path, (newPath) => {
  if (newPath === '/create' && isVisible.value) {
    // 自动关闭并标记为已显示
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, 'true');
    }
    isVisible.value = false;
  }
});

// 开始创作
const handleStartCreating = () => {
  // 标记为已显示
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, 'true');
  }
  isVisible.value = false;

  // 跳转到创作页面
  router.push('/create');
};

// 关闭横幅
const handleClose = () => {
  // 标记为已显示
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, 'true');
  }
  isVisible.value = false;
};
</script>

<style scoped>
/* 横幅容器 - 相对定位，占据文档流空间 */
.welcome-banner-wrapper {
  position: relative;
  width: 100%;
  padding: 0;
  margin-bottom: 1rem;
  z-index: 10;
  /* Safari 特殊处理 */
  -webkit-transform: translate3d(0, 0, 0);
  transform: translate3d(0, 0, 0);
}

@media (min-width: 1024px) {
  .welcome-banner-wrapper {
    padding: 0;
    margin-bottom: 1.5rem;
  }
}

/* 横幅主体 - 使用网站暗黑主题 */
.welcome-banner {
  position: relative;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.15) 100%);
  border: 1px solid rgba(139, 92, 246, 0.3);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 0.75rem;
  padding: 0.75rem;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.1);
  overflow: hidden;
  /* 确保在 Safari 上正确渲染 */
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
}

/* 渐变背景装饰 */
.banner-gradient {
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.1) 0%,
    transparent 70%
  );
  pointer-events: none;
}

/* 内容布局 */
.banner-content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

@media (min-width: 640px) {
  .banner-content {
    flex-wrap: nowrap;
  }
}

/* 图标 */
.banner-icon {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  background: rgba(139, 92, 246, 0.3);
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(196, 181, 253); /* purple-300 */
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.banner-icon div {
  font-size: 1.25rem;
}

/* 文字区域 */
.banner-text {
  flex: 1;
  min-width: 0;
}

.banner-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: rgb(229, 231, 235); /* gray-200 */
  margin: 0;
  line-height: 1.4;
}

.banner-subtitle {
  font-size: 0.8125rem;
  color: rgb(156, 163, 175); /* gray-400 */
  margin: 0.125rem 0 0 0;
  line-height: 1.3;
}

/* 操作按钮 */
.banner-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.btn-primary {
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, rgb(139, 92, 246) 0%, rgb(124, 58, 237) 100%);
  color: white;
  font-weight: 600;
  font-size: 0.8125rem;
  border-radius: 0.375rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  /* Safari 优化 */
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
  background: linear-gradient(135deg, rgb(147, 107, 255) 0%, rgb(132, 70, 245) 100%);
}

.btn-primary:active {
  transform: translateY(0);
}

.btn-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  background: rgba(107, 114, 128, 0.3); /* gray-500 */
  border: none;
  border-radius: 0.375rem;
  color: rgb(209, 213, 219); /* gray-300 */
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  /* Safari 优化 */
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.btn-close:hover {
  background: rgba(107, 114, 128, 0.5);
  color: white;
}

/* 动画 */
.banner-slide-enter-active {
  animation: slideDown 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.banner-slide-leave-active {
  animation: slideUp 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideUp {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-100%);
  }
}

/* 移动端优化 */
@media (max-width: 639px) {
  .welcome-banner {
    padding: 0.875rem;
  }

  .banner-title {
    font-size: 1rem;
  }

  .banner-subtitle {
    font-size: 0.8125rem;
  }

  .btn-primary {
    padding: 0.5rem 1rem;
    font-size: 0.8125rem;
  }

  .banner-actions {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
